import moment from 'moment';
import { TeamOutlined, DollarOutlined } from '@ant-design/icons';
import { LEDGER_RECONCILIATION_FILTER, LEDGER_TYPE_LABELS } from '../../constants/ledger.constants';
import {
    ADVANCE_TYPE_FILTER_OPTIONS,
    LEDGER_RECONCILIATION_FILTER_LABELS,
} from '../common/ledgerTable/ledgerTable.constants';
import { JOURNEY_STAGE_FILTER_OPTIONS } from './elements/journeyStageTag.constants';
import { PAYROLL_STATUS_CHIP_OPTIONS as PAYROLL_STATUS_FILTER_OPTIONS } from '../common/payrollStatusPill/payrollStatus.constants';
import { formatNumberWithCommas } from '../../utils/numbers';

const REPORTS_TABS_ALL = [
    { key: 'users', label: 'Users', icon: <TeamOutlined /> },
    { key: 'transactions', label: 'Transactions', icon: <DollarOutlined /> },
];

export function getReportsTabs(canViewTransactions) {
    if (!canViewTransactions) {
        return REPORTS_TABS_ALL.filter(tab => tab.key !== 'transactions');
    }
    return REPORTS_TABS_ALL;
}

export const REPORTS_TABS = REPORTS_TABS_ALL;

/**
 * Why the enrollment funnel section shows its error panel instead of cards.
 * `LOAD_FAILED`: the fetch threw. `EMPTY`: the server answered with no cards,
 * which is not a valid state either — both keep the Retry button.
 */
export const FUNNEL_ERROR = Object.freeze({
    LOAD_FAILED: 'load_failed',
    EMPTY: 'empty',
});

/** Default page size for both reports tables (server-side pagination). */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Download scopes offered by the header "Download" control (KAN-548). Keys
 * are the `scope` values `POST /api/admin/reports/:aba/export` accepts; the
 * server applies the rule (all = active filters across all time, date = the
 * active range, page = the page/pageSize on screen).
 */
export const EXPORT_SCOPE_KEYS = Object.freeze({
    ALL: 'all',
    DATE: 'date',
    PAGE: 'page',
});

export const EXPORT_SCOPES = Object.freeze([
    { key: EXPORT_SCOPE_KEYS.ALL, label: 'All' },
    { key: EXPORT_SCOPE_KEYS.DATE, label: 'By date range' },
    { key: EXPORT_SCOPE_KEYS.PAGE, label: 'Current page' },
]);

/**
 * "Transactions · By date range" — names what an export covers, so a
 * notification that outlives a tab switch still says which tab and scope it
 * belongs to.
 * @param {{ tab?: string, scope?: string }} target
 * @returns {string}
 */
export function describeExportTarget({ tab, scope } = {}) {
    const tabLabel = REPORTS_TABS_ALL.find((t) => t.key === tab)?.label ?? tab;
    const scopeLabel = EXPORT_SCOPES.find((s) => s.key === scope)?.label ?? scope;
    return [tabLabel, scopeLabel].filter(Boolean).join(' · ');
}

const rowsLabel = (rows) => (rows != null ? `${formatNumberWithCommas(rows)} rows` : null);

/** Copy for the export control and its outcomes. */
export const EXPORT_COPY = Object.freeze({
    BUTTON: 'Download',
    BUTTON_BUSY: 'Preparing…',
    BY_DATE_DISABLED_TOOLTIP: 'Select a date range in the filters to export by date.',
    ALREADY_PREPARING: 'An export is already being prepared. Wait for it to finish before starting another.',
    EMPTY: 'No rows match the current filters — nothing to export.',
    PREPARING_TITLE: 'Preparing your export…',
    PREPARING_DESCRIPTION: (target, rows) =>
        `${[describeExportTarget(target), rowsLabel(rows)].filter(Boolean).join(' — ')}. We will notify you here when the file is ready. You can keep working.`,
    PREPARING_INDICATOR: (target, rows) =>
        `Preparing ${[describeExportTarget(target), rowsLabel(rows)].filter(Boolean).join(' — ')}…`,
    READY_TITLE: 'Your export is ready',
    READY_DESCRIPTION: (target, rows, expiresAt) => {
        const expires = moment(expiresAt ?? NaN);
        const expiry = expires.isValid() ? `at ${expires.format('h:mm A')}` : 'in 15 minutes';
        return `${[describeExportTarget(target), rowsLabel(rows)].filter(Boolean).join(' — ')}. The download link expires ${expiry}.`;
    },
    READY_ACTION: 'Download file',
    FAILED_TITLE: 'Export failed',
    FAILED_DESCRIPTION: 'The export could not be generated. Please try again.',
    TIMED_OUT_TITLE: 'Export is taking longer than expected',
    TIMED_OUT_DESCRIPTION: 'We stopped checking. Request the export again, or try a smaller range.',
    DOWNLOADED: (rows) => (rows != null ? `Downloaded ${rowsLabel(rows)}.` : 'Download started.'),
});

/**
 * Poll cadence and wall-clock ceiling for asynchronous exports. The ceiling
 * matches the service's stale-job window (REPORTS.EXPORT.STALE_JOB_MS): a job
 * the service is still willing to finish is a job we keep collecting.
 */
export const EXPORT_POLL_INTERVAL_MS = 3000;
export const EXPORT_POLL_TIMEOUT_MS = 30 * 60 * 1000;

export const ENROLLMENT_OPTIONS = [
    { value: 'enrolled', label: 'Enrolled (Timeframe)' },
    { value: 'with_activity', label: 'With Advances' },
    { value: 'without_activity', label: 'Without Advances' },
];

export const DD_SWITCH_OPTIONS = [
    { value: 'complete', label: 'Complete' },
    { value: 'incomplete', label: 'Incomplete' },
];

/**
 * Quick-pick chips for the timeframe selector in the filter bar (the
 * "Enrollment Date" dropdowns visible in the design). Mirrors the values
 * `DateFilters` already understands (`all`, `7d`, `30d`, `custom`) plus a
 * friendly label. "custom" exposes the `RangePicker` inside the advanced
 * filters popover, not here.
 */
export const ENROLLMENT_DATE_CHIP_OPTIONS = [
    { value: 'all', label: 'Enrollment Date: All' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
];

/**
 * "Stage" chips in the filter bar — the journey-stage vocabulary from
 * `journeyStageTag.constants`, so the filter offers exactly the values the
 * JOURNEY STAGE column can show. Sent to the users endpoint as `?stage=`.
 */
export const STAGE_CHIP_OPTIONS = JOURNEY_STAGE_FILTER_OPTIONS;

/**
 * "Payroll" chips in the filter bar — the payroll-connection states of the
 * PAYROLL column (`common/payrollStatusPill/payrollStatus.constants`), sent to
 * the users endpoint as `?payrollStatus=`. Replaces the pre-KAN-618 placeholder
 * platform list (Gusto / ADP / Symxv2) and its `?payroll=` param.
 */
export const PAYROLL_STATUS_CHIP_OPTIONS = PAYROLL_STATUS_FILTER_OPTIONS;

/*
 * Funnel card labels/tones/definitions come from the SERVER payload
 * (`GET /api/reports/enrollment-funnel/:aba`) — the admin renders what it is
 * given so copy can never drift. The label-free loading skeleton and the
 * payload normalizer live in `./funnelPayload.js`.
 *
 * Card chrome reference (variant → background fill):
 *  - `default`     : white  — top-of-funnel baseline
 *  - `surface_50`  : #f0f7ff — drop-off steps
 *  - `surface_100` : #e0effe — mid funnel
 *  - `surface_200` : #bfdbfe — late funnel
 *  - `primary`     : #2563eb — terminal conversion step (text rendered white;
 *    `tone` is visually absorbed there)
 *
 * `tone` colors the delta on non-primary cards: neutral (slate-500),
 * warn (amber-600), danger (red-500), success (green). The server computes
 * tone direction-aware: a large share is bad on a drop-off card and good on a
 * success card.
 */

/**
 * URL query keys used to persist the USERS-tab filter state. Kept in one
 * place so the controller (read + write) and any other consumer stay in
 * sync. Note that `tab` is owned by the `Page` component itself and is
 * deliberately NOT cleared when resetting filters.
 *
 * The Transactions tab has its own set below (TRANSACTION_FILTER_PARAM_KEYS):
 * the two tabs filter on different things, and sharing one bag was how a
 * Users filter leaked into a Transactions fetch. Switching tabs drops the
 * other tab's keys from the URL.
 */
export const FILTER_PARAM_KEYS = Object.freeze({
    ENROLLMENT: 'enrollment',
    DD_SWITCH: 'ddSwitch',
    TIME_FRAME: 'timeFrame',
    DATE_START: 'dateStart',
    DATE_END: 'dateEnd',
    STAGE: 'stage',
    PAYROLL_STATUS: 'payrollStatus',
    SEARCH: 'search',
    // Active KPI card (card key) filtering the Users table — sent to the
    // service as `?funnelStage=`. `stage` is taken by the journey-stage chip
    // (a row's single stage); a card is a population a member can be in
    // several of, so it has its own key and a colleague can open the exact view.
    FUNNEL_CARD: 'card',
});

/**
 * Card keys of `GET /api/reports/enrollment-funnel/:aba`, byte-for-byte the
 * service's `Constants.REPORTS.FUNNEL.CARD_KEYS.LIST`. Used only to validate a
 * `?card=` deep link before the cards have loaded; whether a card can filter
 * the table is decided by the payload's per-card `filterable` (computed per
 * caller — e.g. First Advance is off for non-owners), never locally.
 */
export const FUNNEL_CARD_KEYS = Object.freeze([
    'tc_pending',
    'pending_payroll_link',
    'payroll_link_error',
    'verifying_payroll_data',
    'invalid_ownership',
    'dds_pending',
    'enrolled',
    'first_advance',
]);

/** Copy for the KPI-card filter UI (chip row, caption hint, empty state). */
export const FUNNEL_FILTER_COPY = Object.freeze({
    CHIP_PREFIX: 'Card',
    // The cards are all-time; so is the population behind a selected card.
    EMPTY_STATE: 'No users in this card',
    CLICK_HINT: 'click a card to filter the table',
    // For the mismatch warning when a payload carried no range at all.
    RANGE_UNAVAILABLE: 'range unavailable',
});

/**
 * Users-table empty / failed copy. Empty and failed must differ: a failed
 * request must never read as "there are no users" (the card-vs-table check
 * relies on the table total being real before comparing it with a card).
 */
export const USERS_TABLE_COPY = Object.freeze({
    EMPTY: 'No users found.',
    FAILED: 'Users could not be loaded. Use Refresh Data to try again.',
});

/**
 * URL query keys for the Users table sort, so a sort survives a refresh next
 * to the filter params. Cleared together with the filters on "Reset All".
 */
export const SORT_PARAM_KEYS = Object.freeze({
    BY: 'sortBy',
    // Same URL vocabulary as the Reviews screen (`sortBy` / `sortOrder`); the
    // API param stays `sortDir` (see `buildApiFilters`).
    DIR: 'sortOrder',
});

/** Sortable users-table columns (antd column keys with `sorter: true`), all accepted by `?sortBy=`. */
export const USERS_SORT_FIELDS = Object.freeze(['lastTransactionAt', 'name']);

/**
 * Users filter URL keys that no longer mean anything: `status` carried the
 * pre-KAN-557 lifecycle vocabulary (`stage` replaced it); `payroll` carried
 * the pre-KAN-618 placeholder platform slug (`payrollStatus` replaced it).
 * Stripped on load and on reset so a saved link never lies about the active
 * filter.
 */
export const LEGACY_FILTER_PARAM_KEYS = Object.freeze({
    STATUS: 'status',
    PAYROLL: 'payroll',
});

/**
 * Shared shape of the per-user sync detail state — the Reports side panel
 * (`syncDetailByClientId[id]`) and the user page (`syncDetail`) both use it,
 * and `SYNC_DETAIL_ERROR` is the vocabulary of `error` (set by
 * `DataController.fetchUserSyncStatus`).
 *
 * @typedef {{ status: 'idle'|'loading'|'ready'|'error', report: object|null, error: string|null }} SyncDetail
 */
export const SYNC_DETAIL_STATUS = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
});
export const SYNC_DETAIL_ERROR = Object.freeze({
    TIMEOUT: 'timeout',
    LOAD_FAILED: 'load_failed',
});
export const SYNC_DETAIL_IDLE = Object.freeze({ status: SYNC_DETAIL_STATUS.IDLE, report: null, error: null });

export const DEFAULT_FILTERS = Object.freeze({
    timeFrame: 'all',
    dateRange: null,
    enrollment: 'enrolled',
    hasCompleteDDSwitch: null,
    // Inline chips in the filter bar (Figma node 5693:16450). `null` = the
    // chip is on "All" and the param is omitted from the request.
    stage: null,
    payrollStatus: null,
    // Users tab only: the KPI card whose population the table is showing.
    funnelStage: null,
});

// ---------------------------------------------------------------------------
// Transactions tab (KAN-544). Transaction attributes only — no user attributes.
// ---------------------------------------------------------------------------

/** URL query keys for the Transactions tab. `page` / `perPage` are written by the table itself. */
export const TRANSACTION_FILTER_PARAM_KEYS = Object.freeze({
    TYPE: 'type',
    ADVANCE_TYPE: 'advanceType',
    TIME_FRAME: 'timeFrame',
    DATE_START: 'dateStart',
    DATE_END: 'dateEnd',
    RECONCILIATION: 'reconciliation',
    SORT_BY: 'sortBy',
    SORT_DIR: 'sortDir',
    SEARCH: 'search',
});

/** `all` in a chip select means "no filter" and is never sent or written to the URL. */
export const TRANSACTION_FILTER_ALL_VALUE = 'all';

export const DEFAULT_TRANSACTION_FILTERS = Object.freeze({
    /** Comma-separated ledger types as the endpoint names them, or null. */
    type: null,
    /** 'instant' | 'standard' | null */
    advanceType: null,
    /** One of TRANSACTION_TIME_FRAMES; 'all' means no created-date bound. */
    timeFrame: 'all',
    /** `[dayjs, dayjs] | null`, only meaningful when timeFrame is 'custom'. */
    dateRange: null,
    /** One of LEDGER_RECONCILIATION_FILTER; INCLUDE is the server default. */
    reconciliation: LEDGER_RECONCILIATION_FILTER.INCLUDE,
});

/** The time frames `DateFilters` offers, and the only values the URL may carry. */
export const TRANSACTION_TIME_FRAMES = Object.freeze(['all', '7d', '30d', 'custom']);

export const TRANSACTION_TIME_FRAME_LABELS = Object.freeze({
    all: 'All time',
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    custom: 'Custom range',
});

/** Filter control labels and chip prefixes. */
export const TRANSACTION_FILTER_LABELS = Object.freeze({
    TYPE: 'Type',
    ADVANCE_TYPE: 'Advance type',
    RECONCILIATION: 'Reconciliations',
    /** The range filters on the CREATED date (`ledger.created_at`) — what the service applies it to. */
    CREATED_DATE: 'Created date',
    SEARCH: 'Search',
});

/** Input and display format for the created-date range picker. */
export const TRANSACTION_DATE_FORMAT = 'MM/DD/YYYY';

/** Transaction type chip. Values are the endpoint's display types (`settlement`, not `settle`). */
export const TRANSACTION_TYPE_FILTER_OPTIONS = Object.freeze([
    { value: TRANSACTION_FILTER_ALL_VALUE, label: `${TRANSACTION_FILTER_LABELS.TYPE}: All` },
    ...Object.entries(LEDGER_TYPE_LABELS).map(([value, label]) => ({ value, label })),
]);

/** Advance type chip, from the table's options (built on `ADVANCE_TYPE_LABELS` in constants/ledger.constants). */
export const ADVANCE_TYPE_FILTER_CHIP_OPTIONS = Object.freeze([
    { value: TRANSACTION_FILTER_ALL_VALUE, label: `${TRANSACTION_FILTER_LABELS.ADVANCE_TYPE}: All` },
    ...ADVANCE_TYPE_FILTER_OPTIONS,
]);

/**
 * Three-state reconciliation chip: All / Exclude reconciliations / Only
 * reconciliations. The two narrowing labels come from the table's constants;
 * "All" is this control's own "no filter" entry, so the label is defined once.
 */
export const RECONCILIATION_FILTER_CHIP_OPTIONS = Object.freeze([
    { value: LEDGER_RECONCILIATION_FILTER.INCLUDE, label: `${TRANSACTION_FILTER_LABELS.RECONCILIATION}: All` },
    ...Object.entries(LEDGER_RECONCILIATION_FILTER_LABELS).map(([value, label]) => ({ value, label })),
]);

/** Copy for the small-screen filter modal, per tab. */
export const USERS_FILTER_MODAL_COPY = Object.freeze({
    title: 'Filter users',
    description: 'Refine the user list by enrollment, sync state and timeframe.',
});

export const TRANSACTIONS_FILTER_MODAL_COPY = Object.freeze({
    title: 'Filter transactions',
    description: 'Refine the transaction list by type, advance type, reconciliations and created date.',
});
